import { Avatar } from "@/shared/components/atoms/Avatar";
import { ButtonSoft } from "@/shared/components/atoms/ButtonSoft";
import EllipsesMenuChat from "@/shared/components/molecules/EllipsesMenuChat";

import { Conversations } from "@/shared/components/organisms/Conversations";
import { ModalChat } from "@/shared/components/organisms/ModalChat";
import { useDebounce } from "@/shared/hooks/debounce";
import { faImage } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { t } from "i18next";
import { FC, Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectConversation,
  selectConversations,
  selectCurrentConverstaionId,
  setConversation,
  setCurrentConversationId,
} from "@/app/chat/chat.slice";
import { selectUser } from "@/app/auth/auth.slice";
import { MessageItem } from "@/shared/components/molecules/Message";

import {
  useAssignManagerMutation,
  useContactsQuery,
  useCreateMessageMutation,
  useManagerConversationsQuery,
  useManagersQuery,
  useMessagesQuery,
  useSupportConversationsQuery,
  useUpdateConversationMutation,
} from "@/app/chat/chat.api";
import { useTrainingProgressQuery } from "@/app/training-progress/training-progress.api";
import { useNutritionProgressQuery } from "@/app/progress-nutritions/progress-nutritions.api";
import { useUserQuery } from "@/app/users/users.api";
import { ChatClientSummary } from "@/shared/components/organisms/ChatClientSummary";
import { Button } from "@/shared/components/atoms/Button";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { toasts } from "@/shared/utils/toasts";
import { ContactListItem } from "@/shared/components/molecules/ContactListItem";
import { AttachmentPreview } from "@/shared/components/molecules/AttachmentPreview";
import { useSearchParams } from "react-router-dom";
import PreviewRoutine from "@/pages/InformationUserPages/Components/PreviewRoutine";
import {PreviewRoutineDialog} from "@/shared/components/organisms/PreviewRoutineDialog";

export const Chat: FC<any> = () => {
  const [searchParams] = useSearchParams();

  const attachmentsInputRef = useRef<HTMLInputElement>();
  const contentDivRef = useRef<HTMLDivElement>();

  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const conversation = useSelector(selectConversation);
  const conversations = useSelector(selectConversations);
  const currentConversationId = useSelector(selectCurrentConverstaionId);
  const messages = useSelector(
    (state: any) => state.chat.messages[currentConversationId]
  );

  const [conversationsVisible, setConversationsVisible] = useState(false);
  const [assignFormVisible, setAssignFormVisible] = useState(false);
  const [content, setContent] = useState("");
  const [attachments, setAttachments] = useState<FileList>();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 200);
  const [openPreviewRoutine, setOpenPreviewRoutine] = useState(false);

  const support = useMemo(
    () => searchParams.get("support") == "1",
    [searchParams]
  );

  const currentConversation = useMemo(() => {
    return (
      conversation ??
      conversations.find(
        (conversation: any) =>
          conversation.conversationId == currentConversationId
      )
    );
  }, [conversation, conversations, currentConversationId]);

  const managersQuery = useManagersQuery(undefined);
  const contactsQuery = useContactsQuery({ search: debouncedSearch });
  const userQuery = useUserQuery(
    { id: currentConversation?.clientId },
    { skip: !currentConversation }
  );

  const messagesQuery = useMessagesQuery({
    conversationId: currentConversation?.conversationId,
  });
  const managerConversationsQuery = useManagerConversationsQuery(undefined, {
    skip: !!support,
  });
  const supportConversationsQuery = useSupportConversationsQuery(undefined, {
    skip: !support,
  });

  const trainingProgressQuery = useTrainingProgressQuery(
    { userId: currentConversation?.clientId, status: 1},
    { skip: !currentConversation }
  );

  const nutritionProgressQuery = useNutritionProgressQuery(
    { userId: currentConversation?.clientId, status: 1 },
    { skip: !currentConversation }
  );

  const [createMessage, createMessageResult] = useCreateMessageMutation();
  const [updateConversation, updateConversationResult] =
    useUpdateConversationMutation();
  const [assignManager, assignManagerResult] = useAssignManagerMutation();

  const attachmentPreviews = useMemo(() => {
    return Array.from(attachments ?? [])?.map((attachment) => ({
      url: URL.createObjectURL(attachment),
      ext: attachment.name.split(".")[1],
    }));
  }, [attachments]);

  useEffect(() => {
    if (createMessageResult.isError) {
      toasts.danger(
        "Operación no realizada",
        "Ocurrió un error al enviar el mensaje"
      );
    }

    if (createMessageResult.isSuccess) {
      if (!currentConversationId && conversation) {
        /**
         * Se ha seleccionado un usuario desdela lista de contactos, por lo cuál, la conversación
         * no está creada hasta que no se mande un mensaje
         */
        dispatch(
          setCurrentConversationId({
            currentConversationId: createMessageResult.data.conversationId,
          })
        );
        managerConversationsQuery.refetch();
      }
      setContent("");
      setAttachments(undefined);
      if (attachmentsInputRef.current) {
        attachmentsInputRef.current.value = "";
      }
    }
  }, [createMessageResult]);

  useEffect(() => {
    const div = contentDivRef.current;
    if (!div) return;

    div.scrollTop = div.scrollHeight;
  }, [messages, currentConversation]);

  const toggleConversationsVisible = () =>
    setConversationsVisible(!conversationsVisible);

  const toggleAssignFormVisible = () =>
    setAssignFormVisible(!assignFormVisible);


  const handleSend = () => {
    createMessage({
      content,
      conversationId: currentConversation.conversationId,
      clientId: currentConversation.clientId,
      managerId: currentConversation.managerId,
      senderId: user.id,
      files: attachmentsInputRef.current?.files,
    });
  };

  const createRemovePreviewHandler = (attachmentIndex: number) => () => {
    if (
      attachmentsInputRef.current == null ||
      attachmentsInputRef.current == undefined
    )
      return;

    const dataTransfer = new DataTransfer();

    Array.from(attachmentsInputRef.current.files ?? [])
      .filter((_, index) => index != attachmentIndex)
      .forEach((file) => dataTransfer.items.add(file));

    attachmentsInputRef.current.files = dataTransfer.files;
    setAttachments(attachmentsInputRef.current.files);
  };

  return (
    <div className="flex flex-1 flex-col items-center h-0 w-full p-4">
      <div className="flex flex-1 h-0 flex-col w-full sm:max-w-full md:max-w-full lg:max-w-full xl:max-w-full 2xl:max-w-full mx-auto">
        <div className="grid grid-cols-12 grid-rows-1 gap-4 max-h-[calc(100vh-120px)]">
          <div className="col-span-12 lg:col-span-3 lg:col-start-1 md:col-span-5 overflow-y-scroll">
            <ChatClientSummary
              user={
                !support && currentConversation && userQuery.data
                  ? userQuery.data
                  : undefined
              }
              isLoading={
                userQuery.isLoading ||
                trainingProgressQuery.isLoading ||
                nutritionProgressQuery.isLoading
              }
              trainingProgress={trainingProgressQuery.data}
              nutritionProgress={nutritionProgressQuery.data}
              onShowPreview={() => setOpenPreviewRoutine(true)}
            />
          </div>

          <div className="flex flex-col col-span-12 lg:col-span-9 lg:col-start-4 md:col-span-7 md:col-start-6 ml-6 mt-4 md:ml-0 md:mt-0">
            <h2 className="bg-white mb-4 text-lg font-semibold text-gray-700 border-b flex justify-between items-center pb-2">
              <span>{t("chat.app.conversacion")} </span>
              <div className="flex space-x-4">
                {!support && currentConversationId && (
                  <EllipsesMenuChat
                    status={currentConversation?.status}
                    onArchive={() =>
                      updateConversation({
                        conversationId: currentConversation?.conversationId,
                        status: 0,
                      })
                    }
                    onPrioritize={() =>
                      updateConversation({
                        conversationId: currentConversation?.conversationId,
                        status: 2,
                      })
                    }
                    onDeprioritize={() =>
                      updateConversation({
                        conversationId: currentConversation?.conversationId,
                        status: 1,
                      })
                    }
                    onAssign={toggleAssignFormVisible}
                  />
                )}
                <div className="-mt-1">
                  <ButtonSoft
                    text={t("chat.app.init_conversacion")}
                    size="medium"
                    onClick={toggleConversationsVisible}
                  />
                </div>
              </div>
            </h2>
            {currentConversation ? (
              <Fragment>
                <div
                  ref={contentDivRef}
                  className="flex flex-col flex-1 h-0 max-w-full rounded-lg p-4  justify-between overflow-y-scroll"
                >
                  {/* Mensajes */}
                  <div className="flex-1 h-0 flex flex-col gap-2">
                    {messages?.map((message) => (
                      <MessageItem key={message.id} message={message} user={user} />
                    ))}
                  </div>
                </div>

                {currentConversation?.status == 0 ? (
                  <div></div>
                ) : (
                  <Fragment>
                    <div className="flex gap-2 mt-0.5">
                      {attachmentPreviews.map((attachmentPreview, index) => (
                        <AttachmentPreview
                          key={index}
                          onRemove={createRemovePreviewHandler(index)}
                          url={attachmentPreview.url}
                          ext={attachmentPreview.ext}
                        />
                      ))}
                    </div>
                    <div className="bg-white p-4 mt-4 rounded-lg border border-gray-300 shadow-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
                      <div>
                        <textarea
                          id="description"
                          name="description"
                          rows={3}
                          placeholder={t("chat.app.write_message")}
                          className="block w-full resize-none border-0 py-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                          onChange={(event) => setContent(event.target.value)}
                          value={content}
                        />
                      </div>

                      <div className="flex items-center justify-between mt-4">
                        <label
                          htmlFor="attachments"
                          className="cursor-pointer group flex items-center text-gray-400"
                        >
                          <FontAwesomeIcon
                            icon={faImage}
                            className="h-5 w-5 mr-2 group-hover:text-gray-500"
                          />
                          <span className="text-sm font-medium text-gray-500 group-hover:text-gray-600">
                            {t("chat.app.add_image")}
                          </span>
                          <input
                            className="hidden"
                            type="file"
                            id="attachments"
                            accept=".png,.jpg,.jpeg,.mp4,.mov"
                            multiple
                            disabled={currentConversation?.status == 0}
                            onChange={(event) => {
                                const newFiles = event.target.files;
                                if (!newFiles) return;

                                const existingFiles = attachments ? Array.from(attachments) : [];

                                const incomingFiles = Array.from(newFiles);

                                const nonDuplicateFiles = incomingFiles.filter(
                                    (newFile) => !existingFiles.some(
                                        (existingFile) =>
                                            existingFile.name === newFile.name &&
                                            existingFile.size === newFile.size
                                    )
                                );

                                const combinedFiles = [...existingFiles, ...nonDuplicateFiles];

                                const dataTransfer = new DataTransfer();
                                combinedFiles.forEach((file) => dataTransfer.items.add(file));

                                attachmentsInputRef.current.files = dataTransfer.files;
                                setAttachments(dataTransfer.files);
                              }
                            }
                            ref={attachmentsInputRef}
                          />
                        </label>

                        <Button
                          primary
                          onClick={handleSend}
                          iconLeft={faPaperPlane}
                          isLoading={createMessageResult.isLoading}
                        >
                          {t("chat.app.send")}
                        </Button>
                      </div>
                    </div>
                  </Fragment>
                )}
              </Fragment>
            ) : (
              <div>{t("chat.app.not_information_chat")}</div>
            )}
          </div>
        </div>
        <Conversations
          support={support}
          onContactClick={(contact) => {
            dispatch(
              setConversation({
                conversation: {
                  clientId: contact.id,
                  client: contact,
                  manager: user,
                  managerId: user.id,
                  chateable: {},
                  status: 1,
                },
              })
            );
          }}
          onSearchChange={(search) => setSearch(search)}
          search={search}
          contacts={contactsQuery.data ?? []}
          currentConversation={currentConversation}
          onConversationClick={(conversation) => {
            dispatch(
              setCurrentConversationId({
                currentConversationId: conversation?.conversationId,
              })
            );
          }}
          isLoading={managerConversationsQuery.isLoading}
          conversations={conversations}
          user={user}
          visible={conversationsVisible}
          onClose={toggleConversationsVisible}
        />
        <ModalChat
          open={assignFormVisible}
          onClose={toggleAssignFormVisible}
          title={t("chat.app.assign_conversation")}
          cancelButtonText={t("general.cancel")}
        >
          <ul
            role="list"
            className="flex-1 divide-y divide-gray-200 overflow-y-auto"
          >
            {managersQuery.data?.map((manager) => (
              <ContactListItem
                key={manager.id}
                user={manager}
                onClick={() => {
                  assignManager({
                    managerId: manager.id,
                    senderId: user.id,
                    conversationId: currentConversationId,
                  });
                  setAssignFormVisible(false);
                }}
              />
            ))}
          </ul>
        </ModalChat>

        {openPreviewRoutine && trainingProgressQuery.data && (
          <PreviewRoutineDialog
              visible={openPreviewRoutine}
              onClose={() => setOpenPreviewRoutine(false)}
              id={trainingProgressQuery.data.idPlan}
              userId={trainingProgressQuery.data?.userId}
              position={trainingProgressQuery.data.idPlan}
          />
        )}
      </div>
    </div>
  );
};
